import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type { CreatePortalBannerPayload, PortalBanner } from '../model/types';

const getActivePortalBanner = async (): Promise<PortalBanner | null> => {
  const response = await baseAPI.get<PortalBanner | null>(
    '/portal-banner/active',
  );
  return response.data || null;
};

const listPortalBanners = async (): Promise<PortalBanner[]> => {
  const response = await baseAPI.get<PortalBanner[]>('/portal-banner');
  return response.data;
};

const createPortalBanner = async (
  payload: CreatePortalBannerPayload,
): Promise<PortalBanner> => {
  const response = await baseAPI.post<PortalBanner>('/portal-banner', payload);
  return response.data;
};

const deactivatePortalBanner = async (id: string): Promise<PortalBanner> => {
  const response = await baseAPI.post<PortalBanner>(
    `/portal-banner/${id}/deactivate`,
  );
  return response.data;
};

const activatePortalBanner = async (id: string): Promise<PortalBanner> => {
  const response = await baseAPI.post<PortalBanner>(
    `/portal-banner/${id}/activate`,
  );
  return response.data;
};

const dismissPortalBanner = async (): Promise<{ success: boolean }> => {
  const response = await baseAPI.post<{ success: boolean }>(
    '/portal-banner/dismiss',
  );
  return response.data;
};

const deletePortalBanner = async (id: string): Promise<{ success: boolean }> => {
  const response = await baseAPI.delete<{ success: boolean }>(
    `/portal-banner/${id}`,
  );
  return response.data;
};

const invalidatePortalBannerQueries = () => {
  void queryClient.invalidateQueries({
    queryKey: [KEYS.PORTAL_BANNER_ACTIVE],
  });
  void queryClient.invalidateQueries({
    queryKey: [KEYS.PORTAL_BANNERS],
  });
};

export const useGetActivePortalBanner = () => {
  return useQuery({
    queryKey: [KEYS.PORTAL_BANNER_ACTIVE],
    queryFn: getActivePortalBanner,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

export const useListPortalBanners = (enabled = true) => {
  return useQuery({
    queryKey: [KEYS.PORTAL_BANNERS],
    queryFn: listPortalBanners,
    enabled,
  });
};

export const useCreatePortalBannerMutation = () => {
  return useMutation({
    mutationFn: createPortalBanner,
    onSuccess: () => {
      toast.success('Portal banner published');
      invalidatePortalBannerQueries();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ?? 'Failed to publish portal banner',
      );
    },
  });
};

export const useDeactivatePortalBannerMutation = () => {
  return useMutation({
    mutationFn: deactivatePortalBanner,
    onSuccess: () => {
      toast.success('Portal banner deactivated');
      invalidatePortalBannerQueries();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ?? 'Failed to deactivate portal banner',
      );
    },
  });
};

export const useActivatePortalBannerMutation = () => {
  return useMutation({
    mutationFn: activatePortalBanner,
    onSuccess: () => {
      toast.success('Portal banner reactivated');
      invalidatePortalBannerQueries();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ?? 'Failed to reactivate portal banner',
      );
    },
  });
};

export const useDismissPortalBannerMutation = () => {
  return useMutation({
    mutationFn: dismissPortalBanner,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PORTAL_BANNER_ACTIVE],
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ?? 'Failed to dismiss portal banner',
      );
    },
  });
};

export const useDeletePortalBannerMutation = () => {
  return useMutation({
    mutationFn: deletePortalBanner,
    onSuccess: () => {
      toast.success('Portal banner deleted');
      invalidatePortalBannerQueries();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error.response?.data?.message ?? 'Failed to delete portal banner',
      );
    },
  });
};
